import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { TextInput, PasswordInput } from "@mantine/core";
import AuthScreenShell from "../../components/AuthScreenShell";
import { Endpoints } from "../../lib/endpoints";
import { PerformRequest } from "../../lib/usePerformRequest";
import { ButtonLoader } from "../../components/Loader";
import { imgNbaLogo } from "../../assets";
import "./styles.scss";

interface LoginResponse {
  tokens: {
    refresh: string;
    access: string;
  };
  user: {
    id: number;
    email: string;
    first_name: string;
    last_name: string;
    is_active: boolean;
    is_staff: boolean;
    is_lawyer: boolean;
    date_joined: string;
  };
  is_lawyer: boolean;
  is_admin: boolean;
}

interface LoginError {
  error?: string;
  [key: string]: any;
}

export default function Login() {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = React.useState(false);

  const form = useForm({
    initialValues: {
      email: "",
      password: "",
    },
    validate: {
      email: (value: string) => {
        if (!value.trim()) {
          return "Email is required";
        }
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) {
          return "Please enter a valid email address";
        }
        return null;
      },
      password: (value: string) => {
        if (!value.trim()) {
          return "Password is required";
        }
        return null;
      },
    },
  });

  async function onSubmit(values: typeof form.values) {
    setIsSubmitting(true);

    try {
      const endpoint = Endpoints.GetLogin();
      const response = await PerformRequest<LoginResponse>({
        method: endpoint.method,
        route: endpoint.route,
        data: {
          email: values.email.trim(),
          password: values.password,
        },
        doNotUseAuthorization: true,
      });

      if (response.status === 200 && response.data) {
        // Store access token
        if (response.data.tokens?.access) {
          localStorage.setItem("t", response.data.tokens.access);
        }

        notifications.show({
          title: "Success",
          message: "Login successful! Redirecting...",
          color: "green",
        });

        // Navigate to dashboard after a brief delay
        setTimeout(() => {
          navigate("/d");
        }, 500);
      } else {
        // Handle error response
        const errorData = (response.data || {}) as LoginError;
        const errorMessage =
          errorData?.error || "Invalid credentials. Please try again.";

        notifications.show({
          title: "Login Failed",
          message: errorMessage,
          color: "red",
        });
      }
    } catch (error: any) {
      let errorMessage = "An error occurred. Please try again.";

      // Handle different error response structures
      if (error?.response?.data) {
        const errorData = error.response.data;
        if (errorData.error) {
          errorMessage = errorData.error;
        } else if (errorData.message) {
          errorMessage = errorData.message;
        } else if (typeof errorData === "string") {
          errorMessage = errorData;
        }
      } else if (error?.message) {
        errorMessage = error.message;
      }

      notifications.show({
        title: "Login Failed",
        message: errorMessage,
        color: "red",
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <AuthScreenShell>
      <div className="login-container">
        <div className="auth-card">
          <div className="auth-header">
            <img
              src={imgNbaLogo}
              alt="NBA Logo"
              className="w-24 h-24 object-contain mb-4 mx-auto nba-logo"
            />
            <h1 className="auth-title">NBA LAGOS REMUNERATION PORTAL</h1>
            <p className="font-semibold text-gray-900 mt-2">Member Access</p>
            <p className="auth-subtitle mt-2">
              Log in to manage your property transactions, verify compliance
              status, and track professional fee settlements.
            </p>
          </div>
        </div>

        <form
          className="auth-form space-y-4"
          onSubmit={form.onSubmit(onSubmit)}
        >
          <TextInput
            label="Email Address"
            placeholder="Enter your email"
            type="email"
            autoComplete="email"
            disabled={isSubmitting}
            error={form.errors.email}
            {...form.getInputProps("email")}
          />

          <PasswordInput
            label="Password"
            placeholder="Enter your password"
            autoComplete="current-password"
            disabled={isSubmitting}
            error={form.errors.password}
            {...form.getInputProps("password")}
          />

          <button
            className="button button--primary auth-screen__submit transition-transform duration-200 hover:-translate-y-0.5 active:translate-y-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2 flex items-center justify-center gap-2"
            type="submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <ButtonLoader color="white" />
                <span>Logging in...</span>
              </>
            ) : (
              "Login"
            )}
          </button>

          <Link
            className="auth-screen__forgot inline-flex items-center justify-center transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--nba-green)] focus-visible:ring-offset-2"
            to="/forgot-password"
          >
            Forgot Password?
          </Link>
        </form>
      </div>
    </AuthScreenShell>
  );
}
